import { useEffect, useState, useRef } from "react";
import withRouter from "components/Common/withRouter";
import PropTypes from "prop-types";
import { Button, Row, Col, CardBody, Card, Alert, Container, Form, Input, FormFeedback, Label } from "reactstrap";
import i18n from 'i18next';
import { getFirebaseBackend } from "helpers/firebase_helper";

const MFAVerification = ({ resolver, onComplete, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const recaptchaVerifierRef = useRef(null);
  const resolverRef = useRef(null); // Store resolver in a ref

  useEffect(() => {
    const sendVerification = async () => {
      try {
        setLoading(true);
        console.log("Starting reCAPTCHA initialization...");

        // Store resolver in ref instead of sessionStorage
        resolverRef.current = resolver;

        const verifier = await getFirebaseBackend().getRecaptchaVerifier(
          'recaptcha-container',
          async () => {
            console.log("reCAPTCHA callback triggered, sending verification...");
            try {
              const verId = await getFirebaseBackend().sendVerificationMessage(resolver, verifier);
              console.log("Verification ID received:", verId);
              window.sessionStorage.setItem('verificationId', verId); // Store only verificationId
              setVerificationId(verId);
            } catch (err) {
              console.error("Error sending verification:", err);
              setError(err.message || "Failed to send verification code");
            }
          }
        );

        recaptchaVerifierRef.current = verifier;
        console.log("reCAPTCHA verifier initialized:", verifier);

        verifier.verify().catch(err => {
          console.error("Error forcing reCAPTCHA verification:", err);
          setError("reCAPTCHA verification failed. Please try again.");
        });

      } catch (err) {
        console.error("Error initializing reCAPTCHA:", err);
        setError(err.message || "Failed to initialize reCAPTCHA");
      } finally {
        setLoading(false);
      }
    };

    if (resolver) {
      console.log("Resolver received, starting verification process:", resolver);
      sendVerification();
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        console.log("Cleaning up reCAPTCHA verifier...");
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, [resolver]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!resolverRef.current) {
        throw new Error("MFA session expired. Please try logging in again.");
      }
      await getFirebaseBackend().completeMFASignIn(verificationId, verificationCode, resolverRef.current);
      window.sessionStorage.removeItem('verificationId'); // Clean up
      onComplete();
    } catch (err) {
      console.error("Error completing MFA:", err);
      setError(err.message || "Verification failed");
      setLoading(false);
    }
  };

  return (
    <>
      <div id="recaptcha-container"></div>
      <Form onSubmit={handleSubmit}>
        {error && <Alert color="danger">{error}</Alert>}
        {loading && <div>Processing verification...</div>}
        {verificationId && (
          <div className="mb-3">
            <Label>{i18n.t("VERIFICATION_CODE")}</Label>
            <Input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder={i18n.t("AUTHENTICATION.ENTER_VERIFICATION_CODE")}
              required
            />
          </div>
        )}
        <div className="mt-3 d-grid">
        <button
          className="btn btn-primary btn-block"
          type="submit"
          disabled={loading || !verificationId}
        >
          {i18n.t("AUTHENTICATION.VERIFY")}
        </button>
        </div>
        <div className="mt-3 text-center">
          <Button color="link" onClick={onCancel}>
            {i18n.t("CANCEL")}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default withRouter(MFAVerification);

MFAVerification.propTypes = {
  router: PropTypes.object,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  resolver: PropTypes.object.isRequired,
};